/*
 * Add-on products
 * ---------------
 * Checkbox rows in the product info column. Tick to put a product in the
 * basket, untick to take it back out.
 *
 * Rows come from the product's complementary products (Search & Discovery),
 * fetched from sections/addon-products.liquid through the Product
 * Recommendations API — the only route that populates `recommendations`. If the
 * fetch returns nothing, whatever fallback row was rendered with the page stays
 * put; if there is no fallback either, the block hides itself.
 *
 * It deliberately does NOT open the theme's cart popup. The popup covers the top
 * of the page, and firing it every time someone toggles a checkbox they are
 * still deciding about is worse than not showing it. The basket count in the
 * header is updated instead, through the same Section Rendering API call the
 * theme's own product form uses.
 */
(function () {
  'use strict';

  const CART_BUBBLE_SECTION = 'cart-icon-bubble';

  class AddonProduct extends HTMLElement {
    constructor() {
      super();

      this.rows = this.querySelector('[data-addon-rows]');
      this.status = this.querySelector('.addon__status');
      this.errorElement = this.querySelector('.addon__error');

      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.hideIfEmpty();
      this.loadComplementary();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    /* --------------------------------------------------- complementary rows */

    async loadComplementary() {
      if (!this.dataset.url || !this.rows) return;

      try {
        const response = await fetch(this.dataset.url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return;

        const fetched = new DOMParser()
          .parseFromString(await response.text(), 'text/html')
          .querySelector('[data-addon-rows]');

        // Nothing configured for this product: keep the fallback row.
        if (!fetched || !parseInt(fetched.dataset.count || '0', 10)) return;

        this.rows.innerHTML = fetched.innerHTML;
        this.rows.dataset.count = fetched.dataset.count;
      } catch (error) {
        // Keep whatever was rendered with the page.
      } finally {
        this.hideIfEmpty();
      }
    }

    hideIfEmpty() {
      this.hidden = !this.querySelector('.addon__checkbox');
    }

    /* ------------------------------------------------------------- toggling */

    onChange(event) {
      const checkbox = event.target.closest('.addon__checkbox');
      if (!checkbox) return;

      if (checkbox.checked) {
        this.add(checkbox);
      } else {
        this.remove(checkbox);
      }
    }

    async add(checkbox) {
      this.setBusy(checkbox, true);

      const body = {
        id: checkbox.dataset.variantId,
        quantity: parseInt(checkbox.dataset.quantity || '1', 10) || 1,
        sections: CART_BUBBLE_SECTION,
        sections_url: window.location.pathname,
      };

      const json = await this.post(this.dataset.cartAddUrl, body, checkbox);
      if (!json) return;

      // Keep the line key so unticking removes this exact line.
      checkbox.dataset.lineKey = json.key || '';
      this.succeed(checkbox, json.sections, this.dataset.addedLabel);
    }

    async remove(checkbox) {
      this.setBusy(checkbox, true);

      const body = {
        id: checkbox.dataset.lineKey || checkbox.dataset.variantId,
        quantity: 0,
        sections: CART_BUBBLE_SECTION,
        sections_url: window.location.pathname,
      };

      const json = await this.post(this.dataset.cartChangeUrl, body, checkbox);
      if (!json) return;

      checkbox.dataset.lineKey = '';
      this.succeed(checkbox, json.sections, this.dataset.removedLabel);
    }

    async post(url, body, checkbox) {
      try {
        const response = await fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await response.json();

        if (!response.ok || json.status) {
          this.fail(checkbox, json.description || json.message);
          return null;
        }
        return json;
      } catch (error) {
        this.fail(checkbox);
        return null;
      }
    }

    /* --------------------------------------------------------------- state */

    setBusy(checkbox, busy) {
      const row = checkbox.closest('.addon__row');
      if (row) row.toggleAttribute('data-busy', busy);
      checkbox.disabled = busy;
      if (busy) this.clearError();
    }

    succeed(checkbox, sections, message) {
      this.setBusy(checkbox, false);
      this.updateCartCount(sections);
      if (this.status) this.status.textContent = message || '';
    }

    fail(checkbox, message) {
      this.setBusy(checkbox, false);

      // Put the checkbox back to what the basket actually contains.
      checkbox.checked = Boolean(checkbox.dataset.lineKey);

      if (!this.errorElement) return;
      this.errorElement.textContent = message || this.dataset.errorLabel;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }

    updateCartCount(sections) {
      const html = sections && sections[CART_BUBBLE_SECTION];

      // The header's cart link carries id="cart-icon-bubble"; the section the
      // API returns is wrapped in <div class="shopify-section"> and does not
      // contain that id. So take the wrapper's contents, exactly as the theme's
      // own cart-notification.js does.
      const target = document.getElementById(CART_BUBBLE_SECTION);
      if (!html || !target) return;

      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.shopify-section');
      if (fresh) target.innerHTML = fresh.innerHTML;
    }
  }

  if (!customElements.get('addon-product')) {
    customElements.define('addon-product', AddonProduct);
  }
})();
